import type {
  BoardsConfig,
  CreateWidget,
  HomeWidget,
  UpdateWidget,
  WidgetType,
} from "@ronin/contracts";
import { Button, Dialog, Input, Label } from "@ronin/ui";
import { useQuery } from "@tanstack/react-query";
import { Clock, Container, Gauge, Heading, Link2, Minus, Trash2 } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import { ContainerBoxFields } from "./container-boxes.js";
import { ContainerPicker } from "./container-picker.js";
import { boxesOf } from "./container-widget.js";
import { CheckField, FormSection } from "./form-section.js";
import { isValidContainerLink, isValidLink, LinkFields, newLink } from "./link-form.js";
import { hasStats, ServerFields } from "./server-fields.js";
import { serverStatsQuery } from "./server-widget.js";
import { useHomeFeatures } from "./use-home.js";

/** The Boards widget is built in (FR6): it can be edited, never added. */
type AddableType = Exclude<WidgetType, "boards">;

const typeNames: Record<WidgetType, string> = {
  link: "link",
  clock: "clock",
  heading: "heading",
  divider: "divider",
  boards: "Boards widget",
  container: "container",
  server: "server stats",
};

/** What the form edits: a new widget, or an existing one (the Boards widget included). */
type Draft = CreateWidget | { type: "boards"; config: BoardsConfig; hideBackground?: boolean };

const types: Array<{ type: AddableType; name: string; text: string; icon: typeof Clock }> = [
  { type: "link", name: "Link", text: "One link with its icon and name.", icon: Link2 },
  { type: "clock", name: "Clock", text: "The time and date in your timezone.", icon: Clock },
  {
    type: "heading",
    name: "Heading",
    text: "A text heading over a group of tiles.",
    icon: Heading,
  },
  { type: "divider", name: "Divider", text: "A thin line to separate groups.", icon: Minus },
  {
    type: "container",
    name: "Container",
    text: "A Docker container of this server with its state.",
    icon: Container,
  },
  {
    type: "server",
    name: "Server",
    text: "CPU, memory, load and disks of this server.",
    icon: Gauge,
  },
];

/** Container tiles start from the picked container (spec14 FR4). */
const defaults: Record<Exclude<AddableType, "container">, () => CreateWidget> = {
  link: () => ({ type: "link", config: newLink() }),
  clock: () => ({ type: "clock", config: { seconds: false, date: true } }),
  heading: () => ({ type: "heading", config: { text: "" } }),
  divider: () => ({ type: "divider", config: {} }),
  server: () => ({ type: "server", config: {} }),
};

interface WidgetDialogProps {
  open: boolean;
  /** The widget being edited; undefined = add. */
  widget?: HomeWidget;
  onOpenChange: (open: boolean) => void;
  onCreate: (input: CreateWidget) => Promise<unknown>;
  onUpdate: (widget: HomeWidget, patch: UpdateWidget) => Promise<unknown>;
  onDelete: (widget: HomeWidget) => void;
}

/** spec12 FR10: add a widget (pick the type, then its settings) or edit one. */
export function WidgetDialog(props: WidgetDialogProps) {
  const { open, widget, onOpenChange } = props;
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={widget ? `Edit ${typeNames[widget.type]}` : "Add widget"}
      // One width for every step and type: the dialog never changes size while in use.
      className="w-[min(100%-2rem,48rem)]"
    >
      <WidgetFlow key={widget?.id ?? "new"} {...props} onCancel={() => onOpenChange(false)} />
    </Dialog>
  );
}

function WidgetFlow({
  widget,
  onCancel,
  onCreate,
  onUpdate,
  onDelete,
}: WidgetDialogProps & { onCancel: () => void }) {
  const id = useId();
  const [draft, setDraft] = useState<Draft | undefined>(
    widget &&
      ({
        type: widget.type,
        config: widget.config,
        hideBackground: widget.hideBackground,
      } as Draft),
  );
  const [pending, setPending] = useState(false);
  const [pickContainer, setPickContainer] = useState(false);
  const features = useHomeFeatures();
  const stats = useQuery({ ...serverStatsQuery, enabled: features.serverStats });
  // spec14 FR3: widgets the server is not set up for are not offered.
  const offered = types.filter(
    ({ type }) =>
      (type !== "container" || features.docker) && (type !== "server" || features.serverStats),
  );

  if (!draft && pickContainer) {
    return <ContainerPicker onPick={(config) => setDraft({ type: "container", config })} />;
  }

  if (!draft) {
    return (
      <ul className="grid gap-2 sm:grid-cols-2">
        {offered.map(({ type, name, text, icon: Icon }) => (
          <li key={type}>
            <button
              type="button"
              onClick={() =>
                type === "container" ? setPickContainer(true) : setDraft(defaults[type]())
              }
              className="flex h-full w-full items-center gap-3.5 border bg-card px-4 py-3 text-left transition-colors duration-150 hover:bg-muted/40 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
            >
              <Icon className="size-5 text-brand" aria-hidden />
              <span className="flex flex-col">
                <span className="text-sm font-medium text-foreground">{name}</span>
                <span className="text-xs text-muted-foreground">{text}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    );
  }

  const canSave =
    !pending &&
    (draft.type === "link"
      ? isValidLink(draft.config)
      : draft.type === "container"
        ? isValidContainerLink(draft.config)
        : draft.type === "server"
          ? hasStats(draft.config, stats.data?.disks.map((d) => d.label) ?? [])
          : draft.type !== "heading" || draft.config.text.trim() !== "");

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!draft || !canSave) return;
    const input = (
      draft.type === "link" || draft.type === "container"
        ? {
            ...draft,
            config: {
              ...draft.config,
              name: draft.config.name.trim(),
              url: draft.config.url.trim(),
            },
          }
        : draft.type === "heading"
          ? { ...draft, config: { text: draft.config.text.trim() } }
          : draft
    ) as Draft;
    setPending(true);
    try {
      if (widget) {
        await onUpdate(widget, {
          hideBackground: input.hideBackground ?? false,
          config: input.config,
        });
      } else if (input.type !== "boards") {
        // FR9a: new widgets go to the open board; drag them into the top grid if wanted.
        await onCreate(input);
      }
    } catch {
      // The error toast comes from the mutation; keep the dialog open with the input.
    } finally {
      setPending(false);
    }
  }

  const plain = draft.type !== "divider" && draft.type !== "boards" && (
    // FR11b: dividers and the boards row never have a tile background.
    <CheckField
      label="No background"
      checked={draft.hideBackground ?? false}
      onChange={(on) => setDraft({ ...draft, hideBackground: on } as CreateWidget)}
    />
  );

  return (
    <form className="flex flex-col gap-6" onSubmit={submit} noValidate>
      {draft.type === "link" ? (
        <LinkFields value={draft.config} onChange={(config) => setDraft({ ...draft, config })}>
          {plain}
        </LinkFields>
      ) : draft.type === "container" ? (
        <>
          <LinkFields
            urlOptional
            value={draft.config}
            onChange={(link) => setDraft({ ...draft, config: { ...draft.config, ...link } })}
          >
            <p className="text-xs text-muted-foreground">
              Container{" "}
              <span className="font-medium text-foreground">{draft.config.container}</span>
            </p>
            {plain}
          </LinkFields>
          <ContainerBoxFields
            value={boxesOf(draft.config)}
            onChange={(show) => setDraft({ ...draft, config: { ...draft.config, show } })}
          />
        </>
      ) : draft.type === "server" ? (
        <>
          <ServerFields
            value={draft.config}
            onChange={(config) => setDraft({ ...draft, config })}
          />
          <FormSection title="Appearance">{plain}</FormSection>
        </>
      ) : draft.type === "heading" ? (
        <>
          <FormSection title="Text">
            <Label htmlFor={`${id}-text`} className="sr-only">
              Text
            </Label>
            <Input
              id={`${id}-text`}
              value={draft.config.text}
              maxLength={80}
              placeholder="e.g. Media"
              data-autofocus
              onChange={(e) => setDraft({ ...draft, config: { text: e.target.value } })}
            />
          </FormSection>
          <FormSection title="Appearance">{plain}</FormSection>
        </>
      ) : draft.type === "clock" ? (
        <>
          <FormSection title="Show">
            <CheckField
              label="Seconds"
              checked={draft.config.seconds}
              onChange={(on) => setDraft({ ...draft, config: { ...draft.config, seconds: on } })}
            />
            <CheckField
              label="Date (when the tile is at least 3.5rem tall)"
              checked={draft.config.date}
              onChange={(on) => setDraft({ ...draft, config: { ...draft.config, date: on } })}
            />
          </FormSection>
          <FormSection title="Appearance">{plain}</FormSection>
        </>
      ) : draft.type === "boards" ? (
        <p className="text-sm text-muted-foreground">
          One box per board. Resize it by its edges in edit mode.
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">
          A line across the grid. Resize it by its edges in edit mode.
        </p>
      )}

      <div className="flex items-center gap-2 border-t pt-4">
        {widget && widget.type !== "boards" && (
          <Button type="button" variant="destructive" size="sm" onClick={() => onDelete(widget)}>
            <Trash2 />
            Delete
          </Button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSave}>
            {widget ? "Save" : "Add widget"}
          </Button>
        </div>
      </div>
    </form>
  );
}
